import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  AgreementSchema,
  AgreementCreateInputSchema,
  AgreementUpdateInputSchema,
  type Agreement,
  type AgreementCreateInput,
  type AgreementUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `agreement` (accord juridique formalisé, org-wide).
 *
 * Réplique le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`AgreementCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { AgreementCreateInput };

/** Nom de la collection Firestore de la famille agreement. */
export const AGREEMENTS_COLLECTION = 'agreements';

/**
 * Repository typé pour la famille `agreement`.
 * Toute lecture est validée par `AgreementSchema`, toute écriture par
 * `AgreementCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface AgreementRepository {
  create(input: AgreementCreateInput): Promise<Agreement>;
  list(options?: ListOptions): Promise<Agreement[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Agreement | null>;
  update(id: string, patch: AgreementUpdateInput): Promise<Agreement>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Agreement>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Agreement>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Agreement>;
}

const agreementConverter = createConverter<Agreement>((raw: DocumentData) => AgreementSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createAgreementRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): AgreementRepository {
  const col = collection(db, AGREEMENTS_COLLECTION).withConverter(agreementConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Agreement>(db, col, AGREEMENTS_COLLECTION, getActorId);

  return {
    async create(input: AgreementCreateInput): Promise<Agreement> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = AgreementCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.signedAt !== undefined) optional['signedAt'] = fields.signedAt;
      if (fields.effectiveFrom !== undefined) optional['effectiveFrom'] = fields.effectiveFrom;
      if (fields.effectiveUntil !== undefined) optional['effectiveUntil'] = fields.effectiveUntil;
      if (fields.renewalTerms !== undefined) optional['renewalTerms'] = fields.renewalTerms;
      if (fields.obligations !== undefined) optional['obligations'] = fields.obligations;
      if (fields.documentIds !== undefined) optional['documentIds'] = fields.documentIds;
      if (fields.subscriptionId !== undefined) optional['subscriptionId'] = fields.subscriptionId;
      if (fields.opportunityId !== undefined) optional['opportunityId'] = fields.opportunityId;
      if (fields.listingId !== undefined) optional['listingId'] = fields.listingId;
      if (fields.invoiceId !== undefined) optional['invoiceId'] = fields.invoiceId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        contactId: fields.contactId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Agreement);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return AgreementSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Agreement[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Agreement | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: AgreementUpdateInput): Promise<Agreement> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = AgreementUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, AGREEMENTS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`agreement ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
